import {
  Box,
  Link,
  MenuItem,
  Stack,
  styled,
  SvgIcon,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import React, { FC, useEffect, useState } from 'react';
import { AppConfig, AppNavBarMenuOptions } from '@oh-vcp/components-common';
import { useTranslation } from 'react-i18next';
import {
  CollapseArrow,
  ExpandArrow,
  AddressCard,
  VideoMenu,
  CommentDots,
  Calendar,
  Messages,
  Camera
} from '../icons';

const Drawer = styled(MuiDrawer)({
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box'
});

interface AppNavDrawerProps {
  options: AppNavBarMenuOptions;
  open?: boolean;
  variant?: 'permanent' | 'persistent' | 'temporary' | undefined;
  defaultSelected?:
    | 'directory'
    | 'evisit'
    | 'schedule'
    | 'telederm'
    | 'econsult'
    | 'message'
    | undefined;
}

const AppNavDrawer: FC<AppNavDrawerProps> = ({ open, variant, options, defaultSelected }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(open || false);
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const {
    isDirectoryEnabled,
    isEconsultEnabled,
    isEvisitEnabled,
    isMessagingEnabled,
    isScheduleEnabled,
    isTeledermEnabled
  } = options;

  useEffect(() => {
    setIsOpen(open || false);
  }, [open]);

  const toggleExpand = () => {
    setIsOpen(!isOpen);
  };

  const getDrawerWidth = () => {
    if (isOpen) {
      return 80;
    }
    if (isMobile) {
      return 0;
    }
    return 35;
  };

  const HighlightedMenuItem = styled(MenuItem)<{ isActive: boolean }>(({ isActive }) =>
    isActive
      ? {
          borderLeftColor: theme.palette.primary.main,
          borderLeftStyle: 'solid',
          borderLeftWidth: 3,
          paddingLeft: 5
        }
      : { paddingLeft: 5 }
  );

  const MenuItemLink = styled(Link)({
    color: 'inherit',
    textDecoration: 'none',
    '&:hover': {
      color: 'white'
    }
  });

  return (
    <Drawer
      variant={variant || 'permanent'}
      open={isOpen}
      sx={{ width: getDrawerWidth() }}
      PaperProps={{
        sx: {
          width: getDrawerWidth()
        }
      }}>
      <Box sx={{ mt: 10 }}>
        {isDirectoryEnabled && (
          <HighlightedMenuItem
            isActive={defaultSelected === 'directory'}
            key="DIRECTORY"
            title={t('AppNav.menu.directory') as string}>
            <MenuItemLink href={`${AppConfig.baseHubUrl}/#/`} target="_self">
              <Stack direction="column" alignItems="center">
                <SvgIcon component={AddressCard} />
                {isOpen && (
                  <Typography ml={1} mt={-1} pt={0.5} variant="body2">
                    {t('AppNav.menu.directory')}
                  </Typography>
                )}
              </Stack>
            </MenuItemLink>
          </HighlightedMenuItem>
        )}
        {isEvisitEnabled && (
          <HighlightedMenuItem
            isActive={defaultSelected === 'evisit'}
            key="EVISIT"
            title={t('AppNav.menu.evisit') as string}>
            <MenuItemLink href={AppConfig.baseEvisitUrl} target="_self">
              <Stack direction="column" alignItems="center">
                <Box pl={isOpen ? 2 : 0}>
                  <SvgIcon component={VideoMenu} />
                </Box>
                {isOpen && (
                  <Typography ml={1} mt={-1.2} pl={1} variant="body2">
                    {t('AppNav.menu.evisit')}
                  </Typography>
                )}
              </Stack>
            </MenuItemLink>
          </HighlightedMenuItem>
        )}
        {isEconsultEnabled && (
          <HighlightedMenuItem
            isActive={defaultSelected === 'econsult'}
            key="ECONSULT"
            title={t('AppNav.menu.econsult') as string}>
            <MenuItemLink href={AppConfig.baseEconUrl} target="_self">
              <Stack direction="column" alignItems="center">
                <SvgIcon component={CommentDots} />
                {isOpen && (
                  <Typography ml={1} mt={-1.2} pt={0.8} variant="body2">
                    {t('AppNav.menu.econsult')}
                  </Typography>
                )}
              </Stack>
            </MenuItemLink>
          </HighlightedMenuItem>
        )}
        {isScheduleEnabled && (
          <HighlightedMenuItem
            isActive={defaultSelected === 'schedule'}
            key="SCHEDULE"
            title={t('AppNav.menu.schedule') as string}>
            <MenuItemLink href={AppConfig.baseScheduleUrl} target="_blank">
              <Stack direction="column" alignItems="center">
                <SvgIcon component={Calendar} />
                {isOpen && (
                  <Typography ml={1} mt={-0.5} pt={1} variant="body2">
                    {t('AppNav.menu.schedule')}
                  </Typography>
                )}
              </Stack>
            </MenuItemLink>
          </HighlightedMenuItem>
        )}
        {isTeledermEnabled && (
          <HighlightedMenuItem
            isActive={defaultSelected === 'telederm'}
            key="TELEDERM"
            title={t('AppNav.menu.telederm') as string}>
            <MenuItemLink href={AppConfig.baseTeledermUrl} target="_blank">
              <Stack direction="column" alignItems="center">
                <SvgIcon component={Camera} />
                {isOpen && (
                  <Typography ml={1} mt={-1.2} pt={1} variant="body2">
                    {t('AppNav.menu.telederm')}
                  </Typography>
                )}
              </Stack>
            </MenuItemLink>
          </HighlightedMenuItem>
        )}
        {isMessagingEnabled && (
          <HighlightedMenuItem
            title={t('AppNav.menu.message') as string}
            isActive={defaultSelected === 'message'}
            key="MESSAGE">
            <MenuItemLink href={`${AppConfig.baseHubUrl}/#/`} target="_self">
              <Stack direction="column" alignItems="center">
                <SvgIcon component={Messages} />
                {isOpen && (
                  <Typography ml={1} mt={-1.2} pt={0.5} variant="body2">
                    {t('AppNav.menu.message')}
                  </Typography>
                )}
              </Stack>
            </MenuItemLink>
          </HighlightedMenuItem>
        )}
        {!isMobile && (
          <Box pl={1} sx={{ position: 'absolute', bottom: 20 }} onClick={toggleExpand}>
            {isOpen ? <CollapseArrow /> : <ExpandArrow />}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default AppNavDrawer;
